/**
 * Config documents in the SPA (D22, D32): the JSON editors' validation — the
 * same schemas the hosts validate with (`@norna/playout-engine/studio.js`'s
 * `studioConfigSchema`, `@norna/playout-engine/showStyle.js`'s `showStyleConfigSchema`,
 * cross-references included), so an error shows while typing, at its path,
 * rather than as a 422 on save — and the structured summary the studio page
 * shows (the show style's is `lib/showStyle/config.ts`).
 *
 * What is saved is the document as written (defaults NOT filled in), exactly
 * as `acp studios create --file` sends it.
 */
import { MAX_STUDIO_CONFIG_CHARS } from '@norna/api-contract/configDocument.js';
import type { JsonValue } from '@norna/domain/primitives.js';
import { type StudioConfig, studioConfigSchema } from '@norna/playout-engine/studio.js';
import type { z } from 'zod';

export type ConfigProblem = {
  /** Dotted, `(document)` for the whole: `pieceTypes.camera.sourceLayer`, `adlibPanels.0.items.2.action`. */
  readonly path: string;
  readonly message: string;
};

export type JsonDocument = { readonly [key: string]: JsonValue };

export type DocumentCheck<C> = { readonly ok: true; readonly document: JsonDocument; readonly config: C } | { readonly ok: false; readonly problems: readonly ConfigProblem[] };

export type ConfigCheck = DocumentCheck<StudioConfig>;

export const DOCUMENT_PATH = '(document)';

function pathOf(path: readonly PropertyKey[]): string {
  return path.length === 0 ? DOCUMENT_PATH : path.map(String).join('.');
}

export function isJsonObject(value: unknown): value is JsonDocument {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Check an editor's text against `schema`: JSON, an object, within `maxChars`,
 * and valid. `noun` names the document in the messages. Problems are sorted by
 * path so the list does not jump around while typing.
 */
export function checkDocumentText<C>(text: string, schema: z.ZodType<C>, maxChars: number, noun: string): DocumentCheck<C> {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (err) {
    return { ok: false, problems: [{ path: DOCUMENT_PATH, message: `not JSON: ${err instanceof Error ? err.message : String(err)}` }] };
  }
  if (!isJsonObject(json)) return { ok: false, problems: [{ path: DOCUMENT_PATH, message: `a ${noun} is a JSON object` }] };
  const size = JSON.stringify(json).length;
  if (size > maxChars) {
    return { ok: false, problems: [{ path: DOCUMENT_PATH, message: `serialises to ${String(size)} characters; at most ${String(maxChars)}` }] };
  }
  const parsed = schema.safeParse(json);
  if (parsed.success) return { ok: true, document: json, config: parsed.data };
  const problems = parsed.error.issues.map((issue) => ({ path: pathOf(issue.path), message: issue.message }));
  return { ok: false, problems: problems.sort((a, b) => a.path.localeCompare(b.path)) };
}

/** Check the studio editor's text: a valid studio config (devices, mappings, settings; D32). */
export function checkConfigText(text: string): ConfigCheck {
  return checkDocumentText(text, studioConfigSchema, MAX_STUDIO_CONFIG_CHARS, 'studio config');
}

/**
 * The server's `validation-failed` issues (paths prefixed `config.`, and
 * `/`-separated by some hosts) as editor problems.
 */
export function serverProblems(issues: readonly { readonly path: string; readonly message: string }[]): ConfigProblem[] {
  return issues.map((issue) => {
    const dotted = issue.path.replaceAll('/', '.').replace(/^\./, '');
    const path = dotted.startsWith('config.') ? dotted.slice('config.'.length) : dotted === 'config' ? DOCUMENT_PATH : dotted;
    return { path: path === '' ? DOCUMENT_PATH : path, message: issue.message };
  });
}

// ─── the structured summary ───

export type StudioSummaryView = {
  readonly devices: readonly { readonly id: string; readonly type: string; readonly standbyPolicy: string; readonly connection: string }[];
  readonly mappings: readonly { readonly layer: string; readonly device: string; readonly lookahead: string; readonly oneShot: boolean }[];
  readonly minimumTakeSpanMs: number;
};

/** `host:port` when the options say so, else the options as JSON. */
function connectionOf(options: Readonly<Record<string, JsonValue>>): string {
  const host = options['host'];
  const port = options['port'];
  if (typeof host === 'string' && (typeof port === 'number' || typeof port === 'string')) return `${host}:${String(port)}`;
  return Object.keys(options).length === 0 ? '–' : JSON.stringify(options);
}

export function studioSummary(config: StudioConfig): StudioSummaryView {
  return {
    devices: Object.entries(config.devices).map(([id, d]) => ({ id, type: d.type, standbyPolicy: d.standbyPolicy, connection: connectionOf(d.options) })),
    mappings: Object.entries(config.mappings).map(([layer, m]) => ({ layer, device: m.device, lookahead: m.lookahead, oneShot: m.oneShot })),
    minimumTakeSpanMs: config.settings.minimumTakeSpanMs,
  };
}

/** A config document pretty-printed for the editor. */
export function configText(document: Readonly<Record<string, JsonValue>>): string {
  return JSON.stringify(document, null, 2);
}
